import {useCallback} from "react";
import {useMessages} from "i18n";
import {arrivalReturnPath, focusLandmarkPath} from "components/sheetOpener";
import {CouponClaimDialogView} from "offers/ladder/CouponClaimDialog";
import type {PromoGift} from "./PromoOfferApi";
import {usePromoClaim} from "./usePromoClaim";
import {usePromoLinkOffer} from "./usePromoLinkOffer";

/**
 * The coupon dialog a promo link opens, on any acquisition platform (kitecorp/transferify-ui#2343).
 *
 * <b>On the app SHELL, beside the referral picker and for its reasons:</b> a shared link can point at
 * any public route, and the sign-in a guest Subscribe raises completes in place wherever the reader
 * is standing. Renders nothing at all without a verified `?promo=` offer.
 *
 * <b>The existing coupon view, with no countdown.</b> This offer has no deadline, so no expiry copy
 * and no clock appear (owner decision on the ticket); scarcity is the api's own `discountsLeft`
 * answer when it gives one. The Stripe sheet, the sold-out state and the close rules are all the
 * shared view's.
 *
 * <b>It owns its own focus restore</b>, exactly like the referral picker's mount: opened by the
 * ARRIVAL, it has no opener for MUI's trap to restore to, so the exit hands focus to the landmark
 * path instead of parking a keyboard reader on the document body.
 */
export function PromoLinkDialog() {
	const promo = usePromoLinkOffer();
	return promo.offer === null ? null : (
		<PromoClaimDialog offer={promo.offer} platform={promo.platform} open={promo.open}
			activeVisit={promo.activeVisit} onClose={promo.dismiss}/>
	);
}

/** Mounted only once an offer exists, so the claim hook always has its code. */
function PromoClaimDialog({offer, platform, open, activeVisit, onClose}: {
	offer: PromoGift; platform: string | null; open: boolean; activeVisit: () => boolean; onClose: () => void;
}) {
	const {offerLadder} = useMessages();
	const claim = usePromoClaim(offer, open, activeVisit, onClose);
	const restoreFocus = useCallback(() => {
		focusLandmarkPath(arrivalReturnPath(document));
	}, []);
	return <CouponClaimDialogView gift={offer.gift} discountsLeft={offer.discountsLeft} claim={claim}
		eyebrow={platform === null ? undefined : offerLadder.gift.platformEyebrow(platform)}
		onClose={claim.dismiss} open={open} onTransitionExited={restoreFocus}/>;
}
